import React from 'react';
import { Grid, Box } from '@mui/material';

import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';
import SkippableFieldIndicator from 'modules/views/shared/SkippableFieldIndicator';
import { WorkerProfileField } from 'types/profileCustomFields';

import { IEmergencyContactConfig } from '../hooks/useSectionsConfig';
import { IContactFormState } from '../types';

interface IEmergencyContactSectionProps {
  config: IEmergencyContactConfig;
  contactForm: IContactFormState;
  formErrors: Record<string, { message: string }>;
  onFieldChange: (field: WorkerProfileField, value: string) => void;
  onPhoneChange: (field: WorkerProfileField) => (value: { phone: string; inputValue: string; country: any }) => void;
  isSubmitting: boolean;
  sectionClasses: string;
  sectionTitleClasses: string;
  labels: {
    emergencyContactName: string;
    emergencyContactRelationship: string;
    emergencyContactMobileNumber: string;
    emergencyContactTitle: string;
  };
  workerProfileFields: {
    emergencyContactName: WorkerProfileField;
    emergencyContactMobilePhone: WorkerProfileField;
    emergencyContactRelationship: WorkerProfileField;
  };
  isArchivedProject?: boolean;
}

const EmergencyContactSection: React.FC<IEmergencyContactSectionProps> = ({
  config,
  contactForm,
  formErrors,
  onFieldChange,
  onPhoneChange,
  isSubmitting,
  sectionClasses,
  sectionTitleClasses,
  labels,
  workerProfileFields,
  isArchivedProject,
}) => {
  // Remove useTranslation - using centralized labels instead

  if (!config.isVisible) return null;

  return (
    <Grid item xs={12} className={sectionClasses}>
      <p className={sectionTitleClasses}>{labels.emergencyContactTitle}</p>
      <Grid container spacing={3}>
        {config.emergencyContactName.isVisible && (
          <Grid item xs={12} sm={6}>
            <Box>
              <TextField
                label={labels.emergencyContactName}
                required={config.emergencyContactName.isMandatory}
                name="emergencyContactName"
                testId="emergencyContactName-input"
                autoComplete="new-password"
                value={contactForm.emergencyContactName}
                error={formErrors.emergencyContactName?.message || ''}
                readOnly={isSubmitting}
                disabled={isArchivedProject}
                onChange={
                  !isSubmitting
                    ? e => onFieldChange(workerProfileFields.emergencyContactName, e.target.value)
                    : undefined
                }
              />
              <SkippableFieldIndicator show={config.emergencyContactName.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {config.emergencyContactRelationship.isVisible && (
          <Grid item xs={12} sm={6}>
            <Box>
              <TextField
                label={labels.emergencyContactRelationship}
                required={config.emergencyContactRelationship.isMandatory}
                name="emergencyContactRelationship"
                testId="emergencyContactRelationship-input"
                autoComplete="new-password"
                value={contactForm.emergencyContactRelationship}
                error={formErrors.emergencyContactRelationship?.message || ''}
                readOnly={isSubmitting}
                disabled={isArchivedProject}
                onChange={
                  !isSubmitting
                    ? e => onFieldChange(workerProfileFields.emergencyContactRelationship, e.target.value)
                    : undefined
                }
              />
              <SkippableFieldIndicator show={config.emergencyContactRelationship.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {config.emergencyContactNumber.isVisible && (
          <Grid item xs={12} sm={6}>
            <Box>
              <MobileInput
                label={labels.emergencyContactMobileNumber}
                required={config.emergencyContactNumber.isMandatory}
                placeholder={labels.emergencyContactMobileNumber}
                testId="emergencyContactNumber-input"
                autoComplete="new-password"
                value={contactForm.emergencyContactNumber}
                error={formErrors.emergencyContactNumber?.message || ''}
                disabled={isArchivedProject}
                readOnly={isSubmitting}
                onChange={!isSubmitting ? onPhoneChange(workerProfileFields.emergencyContactMobilePhone) : () => {}}
              />
              <SkippableFieldIndicator show={config.emergencyContactNumber.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default EmergencyContactSection;
